/**
 * Shared Response Types
 * Used by both backend and frontend for consistent error/success handling
 */

// ============================================
// Error Types
// ============================================

export enum ErrorTypes {
  BadRequest = 'BadRequest',
  NotFound = 'NotFound',
  Unauthorized = 'Unauthorized',
  Forbidden = 'Forbidden',
  Conflict = 'Conflict',
  ValidationError = 'ValidationError',
  InternalServerError = 'InternalServerError',
  BadGateway = 'BadGateway',
  ServiceUnavailable = 'ServiceUnavailable',
}

export const errorTypeToStatusCode: Record<ErrorTypes, number> = {
  [ErrorTypes.BadRequest]: 400,
  [ErrorTypes.Unauthorized]: 401,
  [ErrorTypes.Forbidden]: 403,
  [ErrorTypes.NotFound]: 404,
  [ErrorTypes.Conflict]: 409,
  [ErrorTypes.ValidationError]: 422,
  [ErrorTypes.InternalServerError]: 500,
  [ErrorTypes.BadGateway]: 502,
  [ErrorTypes.ServiceUnavailable]: 503,
};

export const statusCodeToErrorType: Record<number, ErrorTypes> = {
  400: ErrorTypes.BadRequest,
  401: ErrorTypes.Unauthorized,
  403: ErrorTypes.Forbidden,
  404: ErrorTypes.NotFound,
  409: ErrorTypes.Conflict,
  422: ErrorTypes.ValidationError,
  500: ErrorTypes.InternalServerError,
  502: ErrorTypes.BadGateway,
  503: ErrorTypes.ServiceUnavailable,
};

export type ErrorType = keyof typeof ErrorTypes;

export type ErrorValidation = { [key: string]: string };

export interface ErrorResponse {
  errorType: ErrorType;
  errorMessage: string;
  errorRaw?: unknown;
  errorsValidation?: ErrorValidation[] | null;
  stack?: string;
}

// ============================================
// Success Types
// ============================================

export enum SuccessTypes {
  OK = 200,
  CREATED = 201,
  ACCEPTED = 202,
  NO_CONTENT = 204,
  RESET_CONTENT = 205,
  PARTIAL_CONTENT = 206,
  MULTI_STATUS = 207,
  ALREADY_REPORTED = 208,
  IM_USED = 226,
}

export interface SuccessResponse<T = unknown> {
  httpStatusCode: SuccessTypes;
  message: string;
  data?: T;
}

// ============================================
// Frontend Error Interface (for axios errors)
// ============================================

export interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      error?: string;
      status?: number;
      errorMessage?: string;
      errorType?: ErrorType;
    };
    status?: number;
  };
  message?: string;
}

// ============================================
// Machine-readable Error Codes
// ============================================

/**
 * Codes carried in an error's `errorRaw` so a client can render a specific
 * recovery path — a banner, a button — instead of matching on `errorMessage`,
 * which is human-facing and translated and so fragile to match on.
 */
export enum ErrorCodes {
  /** The email belongs to a soft-deleted member: restore it, don't re-invite. */
  EmployeeDeleted = 'EMPLOYEE_DELETED',
}

export interface EmployeeDeletedError {
  code: ErrorCodes.EmployeeDeleted;
  email: string;
}

export const isEmployeeDeletedError = (
  errorRaw: unknown,
): errorRaw is EmployeeDeletedError =>
  typeof errorRaw === 'object' &&
  errorRaw !== null &&
  (errorRaw as { code?: unknown }).code === ErrorCodes.EmployeeDeleted &&
  typeof (errorRaw as { email?: unknown }).email === 'string';
